import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plane, Plus, Trash2 } from "lucide-react";

export default function FlightsLog({ trip, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ airline: "", flight_no: "", from_airport: "", to_airport: "", departure_at: "", arrival_at: "", confirmation: "" });
  const save = async () => {
    if (!f.airline || !f.flight_no) { toast.error("Airline & flight # required"); return; }
    try { const { data } = await api.post(`/trips/${trip.trip_id}/flights`, f); onUpdate(data); setOpen(false); setF({ airline: "", flight_no: "", from_airport: "", to_airport: "", departure_at: "", arrival_at: "", confirmation: "" }); toast.success("Flight added"); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    try { const { data } = await api.delete(`/trips/${trip.trip_id}/flights/${id}`); onUpdate(data); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-display text-2xl font-semibold">Flights log</h2>
        <Button size="sm" onClick={()=>setOpen(true)} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="add-flight-button"><Plus size={14} className="mr-1"/> Add flight</Button>
      </div>
      {trip.flights.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-stone-300 rounded-2xl bg-white">
          <Plane size={26} className="mx-auto text-stone-400 mb-2"/>
          <div className="text-stone-500">No flights logged yet.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {trip.flights.map(fl => (
            <div key={fl.id} className="relative rounded-2xl border border-stone-200 bg-white overflow-hidden group" data-testid={`flight-${fl.id}`}>
              <div className="p-5 flex items-center justify-between border-b border-dashed border-stone-300">
                <div>
                  <div className="font-mono-code text-[10px] uppercase tracking-widest text-sky-700">{fl.airline}</div>
                  <div className="font-serif-display text-3xl font-bold">{fl.flight_no}</div>
                </div>
                <Plane className="text-sky-600" size={28}/>
              </div>
              <div className="p-5 grid grid-cols-3 gap-3 items-center">
                <div>
                  <div className="font-mono-code text-xs text-stone-500 uppercase">From</div>
                  <div className="font-serif-display text-xl">{fl.from_airport || "—"}</div>
                  <div className="font-mono-code text-xs text-stone-500 mt-1">{fl.departure_at}</div>
                </div>
                <div className="text-center text-stone-400">✈</div>
                <div className="text-right">
                  <div className="font-mono-code text-xs text-stone-500 uppercase">To</div>
                  <div className="font-serif-display text-xl">{fl.to_airport || "—"}</div>
                  <div className="font-mono-code text-xs text-stone-500 mt-1">{fl.arrival_at}</div>
                </div>
              </div>
              <div className="px-5 pb-4 flex items-center justify-between">
                <div className="font-mono-code text-xs text-stone-500">Conf: {fl.confirmation || "—"}</div>
                <button onClick={()=>del(fl.id)} data-testid={`flight-delete-${fl.id}`} className="text-stone-400 hover:text-red-600 opacity-0 group-hover:opacity-100"><Trash2 size={16}/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[#FAF7F2]">
          <DialogHeader><DialogTitle className="font-serif-display text-2xl">Log flight</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Airline</Label><Input value={f.airline} onChange={(e)=>setF({...f, airline: e.target.value})} className="mt-1" data-testid="flight-airline-input"/></div>
            <div><Label>Flight #</Label><Input value={f.flight_no} onChange={(e)=>setF({...f, flight_no: e.target.value})} className="mt-1" data-testid="flight-number-input"/></div>
            <div><Label>From (IATA)</Label><Input value={f.from_airport} onChange={(e)=>setF({...f, from_airport: e.target.value})} className="mt-1"/></div>
            <div><Label>To (IATA)</Label><Input value={f.to_airport} onChange={(e)=>setF({...f, to_airport: e.target.value})} className="mt-1"/></div>
            <div><Label>Departure</Label><Input type="datetime-local" value={f.departure_at} onChange={(e)=>setF({...f, departure_at: e.target.value})} className="mt-1"/></div>
            <div><Label>Arrival</Label><Input type="datetime-local" value={f.arrival_at} onChange={(e)=>setF({...f, arrival_at: e.target.value})} className="mt-1"/></div>
            <div className="col-span-2"><Label>Confirmation</Label><Input value={f.confirmation} onChange={(e)=>setF({...f, confirmation: e.target.value})} className="mt-1"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#C85A32] hover:bg-[#B04B26]" data-testid="flight-save-button">Save flight</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
